import { Component, OnInit, ViewChild, ViewContainerRef } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { EmployeeService } from '../../services/employee/employee.service'
import { Store } from '@ngrx/store'
import { AppState } from '../../store/state/app.state'
import { DatePipe } from '@angular/common'
import { getEmployeeFromState } from '../../store/selector/employee.selector'
import { dateOfBirthValidator, emailValidator } from '../../validators/validators.validator'
import { GlobalConstants } from '../../constants/global-constants'
import { EmployeeState } from '../../store/state/employee.state'
import { Observable, Subscription } from 'rxjs'
import { getCompanySettingsFromState, getUserFromState } from '../../store/selector/initialize.selector'
import { InitializeService } from '../../services/initialize/initialize.service'
import { CompanySettingPreferencesAPIModel, EmployeeAPIModel, UserAPIModel } from '../../models/api.model'
import { CommonService } from 'src/app/services/common/common.service'

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit {
  @ViewChild('targetContainer', { read: ViewContainerRef }) targetContainer: ViewContainerRef
  employeeData$: Observable<EmployeeState>
  employeeDataSubscription: Subscription
  companySettingSubscription: Subscription
  employeeSettingsForm: FormGroup
  communicationForm: FormGroup
  moduleForm: FormGroup
  employeeDataFromState: EmployeeAPIModel
  empCommunicationPreferences: string[]
  companyGroupId: string

  readonly COMPANY_SETTINGS_TYPE = GlobalConstants.COMPANY_SETTINGS_TYPE
  readonly WHATSAPP = GlobalConstants.NOTIFICATION_PREFERENCES.WHATSAPP.toLowerCase()
  readonly SMS = GlobalConstants.NOTIFICATION_PREFERENCES.SMS.toLowerCase()
  readonly EMAIL = GlobalConstants.NOTIFICATION_PREFERENCES.EMAIL.toLowerCase()
  readonly ENABLED = 'Enabled'
  readonly DISABLED = 'Disabled'
  communicationPreferences: string[] = []
  private readonly subscriptions$: Subscription = new Subscription()
  private user: UserAPIModel

  constructor(
    private formBuilder: FormBuilder,
    private employeeService: EmployeeService,
    private initializeService: InitializeService,
    private appState: Store<AppState>,
    private datePipe: DatePipe,
    private cs: CommonService
  ) {}

  ngOnInit(): void {
    this.buildSettingsForm()
    this.buildCommunicationForm()
    this.subscriptions$.add(
      this.appState.select(getUserFromState).subscribe((user: UserAPIModel) => {
        if (user) {
          this.user = user
        }
      })
    )
  }

  private buildSettingsForm() {
    this.employeeService.dispatchGetEmployeePersonalInfoData()
    this.employeeData$ = this.appState.select(getEmployeeFromState)

    this.employeeDataSubscription = this.employeeData$.subscribe((employee) => {
      this.companyGroupId = employee?.companyGroupId
      this.empCommunicationPreferences = employee?.notificationPreferences
      this.employeeDataFromState = employee
      this.fetchCompanySettings()
      this.employeeSettingsForm = this.formBuilder.group({
        firstName: [
          { value: employee?.firstName ? employee.firstName : '', disabled: true },
          [Validators.required, Validators.minLength(5), Validators.maxLength(50), Validators.pattern('^[a-zA-Z ]*$')]
        ],
        middleName: [
          { value: employee?.middleName ? employee.middleName : '', disabled: true },
          [Validators.required, Validators.minLength(5), Validators.maxLength(50), Validators.pattern('^[a-zA-Z ]*$')]
        ],
        lastName: [
          { value: employee?.lastName ? employee.lastName : '', disabled: true },
          [Validators.required, Validators.minLength(5), Validators.maxLength(50), Validators.pattern('^[a-zA-Z ]*$')]
        ],
        employeeCode: [
          { value: employee?.employeeCode ? employee.employeeCode : '', disabled: true },
          Validators.required
        ],
        companyEntityId: [employee?.companyEntityId ? employee.companyEntityId : '', Validators.required],
        doj: [
          {
            value: employee?.doj
              ? this.datePipe.transform(new Date(employee.doj), 'dd/MM/yyy', GlobalConstants.IST_OFFSET)
              : '',
            disabled: true
          },
          Validators.required
        ],
        dob: [
          {
            value: employee?.dob
              ? this.datePipe.transform(new Date(employee.dob), 'dd/MM/yyy', GlobalConstants.IST_OFFSET)
              : '',
            disabled: true
          },
          [Validators.required, dateOfBirthValidator(GlobalConstants.employeeMinAge)]
        ],
        maritalStatus: [
          { value: employee?.maritalStatus ? employee.maritalStatus : '', disabled: true },
          Validators.required
        ],
        gender: [{ value: employee?.gender ? employee.gender : '', disabled: true }, Validators.required],
        primaryEmailId: [
          { value: employee?.emailId ? employee.emailId : '', disabled: true },
          [Validators.required, Validators.minLength(5), Validators.maxLength(50), emailValidator()]
        ],
        location: [
          {
            value: employee?.employeeEmploymentBasicDetails?.location
              ? employee.employeeEmploymentBasicDetails.location
              : '',
            disabled: true
          }
        ]
      })
    })
  }

  buildCommunicationForm() {
    this.communicationForm = this.formBuilder.group({
      [this.EMAIL]: [{ value: true }],
      [this.SMS]: [{ value: false }],
      [this.WHATSAPP]: [{ value: false }]
    })
    this.updatePreferenceFormsBasedOnSettings()
  }

  fetchCompanySettings() {
    if (this.companyGroupId) {
      this.initializeService.dispatchFetchCompanySettingsData(this.companyGroupId)
    }
  }

  updatePreferenceFormsBasedOnSettings() {
    this.companySettingSubscription = this.appState
      .select(getCompanySettingsFromState)
      ?.subscribe((companySettings: CompanySettingPreferencesAPIModel) => {
        const communicationPreferences = companySettings?.notificationPreferences
        if (communicationPreferences?.length > 0) {
          this.updateFormControlsFromPreferences(this.communicationForm, communicationPreferences)
        }
        const modulePreferences = companySettings?.modules
        if (modulePreferences?.length > 0) {
          this.updateFormControlsFromPreferences(this.moduleForm, modulePreferences)
        }
        this.communicationPreferences = communicationPreferences
      })
  }

  updateFormControlsFromPreferences(form: FormGroup, preferences: string[]): void {
    if (!form || !preferences) {
      return
    }
    const lowercasePreferencesFromCompany = preferences?.map((ele) => ele.toLowerCase())
    const empCommsPref = this.empCommunicationPreferences?.map((ele) => ele.toLowerCase())
    Object.keys(form.controls).forEach((controlName) => {
      const control = form.get(controlName)
      if (control) {
        if (form === this.communicationForm) {
          control.setValue(empCommsPref?.includes(controlName))
          !lowercasePreferencesFromCompany.includes(controlName) ? control.disable() : control.enable()
        } else {
          control.setValue(lowercasePreferencesFromCompany.includes(controlName))
          control.disable()
        }
      }
    })
  }

  saveEmployeeNotificationPreference() {
    const preferences = Object.keys(this.communicationForm.controls)
      .filter(
        (controlName) =>
          this.communicationForm.controls[controlName].value === true &&
          !this.communicationForm.controls[controlName].disabled
      )
      .map((controlName) => controlName.toUpperCase())

    const payload: EmployeeAPIModel = { ...this.employeeDataFromState, notificationPreferences: preferences }
    this.employeeService.dispatchUpdateEmployeePersonalInfoAction(payload)
  }

  getSave() {}

  openChangePassword() {}

  ngOnDestroy() {
    this.employeeDataSubscription?.unsubscribe()
    this.companySettingSubscription?.unsubscribe()
    this.subscriptions$.unsubscribe()
  }

  setPreferences(type: string) {
    type = type.toUpperCase()
    if (type === this.EMAIL.toUpperCase()) {
      if (!this.communicationForm.get(this.EMAIL)?.value) {
        this.cs.showError(`You cannot disable the default communication mode`)
      }
      this.communicationForm.get(this.EMAIL)?.setValue(true)
    } else if (type === this.SMS.toUpperCase()) {
      if (!this.user?.mobileNumber && this.communicationForm.get(this.SMS)?.value) {
        this.cs.showError(`Update your mobile number in My Profile page to enable SMS notifications`)
        this.communicationForm.get(this.SMS)?.setValue(false)
      }
    } else {
      if (!this.communicationPreferences.includes(type.toUpperCase())) {
        this.cs.showError(`You cannot enable ${type} communication mode`)
      }
    }
  }
}
